<template>
  <div>
    <v-card flat class="pa-4" id="report-container">
      <!-- Toolbar (hidden when printing) -->
      <div class="no-print">
        <v-row class="mb-4" no-gutters>
          <v-col cols="6">
            <v-btn text @click="$router.push('/claimsv3')">
              <v-icon left>mdi-arrow-left</v-icon> Back to Dashboard
            </v-btn>
          </v-col>
          <v-col cols="6" class="text-right">
            <v-btn color="primary" class="mr-2" @click="printPDF" :disabled="!claimRows.length">
              <v-icon left>mdi-file-pdf-box</v-icon> PDF
            </v-btn>
            <v-btn color="success" class="mr-2" @click="exportXLSX" :disabled="!claimRows.length">
              <v-icon left>mdi-file-excel</v-icon> XLSX
            </v-btn>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <v-btn color="blue-grey" disabled>
                    <v-icon left>mdi-file-word</v-icon> DOCX
                  </v-btn>
                </span>
              </template>
              <span>SOON</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </div>

      <!-- ===== CLAIM SELECTOR (always visible, no-print) ===== -->
      <div class="no-print mb-4">
        <v-card flat class="pa-4">
          <v-card-title class="pa-0 mb-3">
            <v-icon left large color="primary">mdi-file-search</v-icon>
            <span class="text-h5">Select Claims for Report</span>
            <v-chip v-if="accumulatedIds.length" color="success" class="ml-3">{{ accumulatedIds.length }} selected</v-chip>
            <v-btn v-if="accumulatedIds.length" text small color="error" class="ml-2" @click="clearSelection">Clear</v-btn>
          </v-card-title>
          <v-row>
            <v-col cols="12" sm="3">
              <v-text-field v-model="searchQuery" label="Case ID or Vehicle No" dense clearable @keydown.enter="searchClaims" />
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field v-model="searchAdjuster" label="Adjuster name" dense clearable @keydown.enter="searchClaims" />
            </v-col>
            <v-col cols="12" sm="2">
              <v-select v-model="searchStatus" :items="statusOptions" label="Status" dense clearable />
            </v-col>
            <v-col cols="12" sm="2">
              <v-btn color="primary" @click="searchClaims" :loading="searching" block>
                <v-icon left>mdi-magnify</v-icon> Search
              </v-btn>
            </v-col>
            <v-col cols="12" sm="2" v-if="accumulatedIds.length">
              <v-btn color="success" @click="generateReport" block>
                <v-icon left>mdi-file-document</v-icon> Generate Report
              </v-btn>
            </v-col>
          </v-row>

          <v-data-table
            v-if="searchResults.length"
            :headers="searchHeaders"
            :items="searchResults"
            :loading="searching"
            item-key="id"
            :items-per-page="10"
            class="mt-3"
            dense
            :footer-props="{ itemsPerPageOptions: [10, 20, 50] }"
          >
            <template v-slot:[`item.selected`]="{ item }">
              <v-checkbox
                :input-value="isSelected(item.id)"
                hide-details
                @click="toggleClaim(item)"
              />
            </template>
            <template v-slot:[`item.aasbRef`]="{ item }">{{ item.aasbRef }}</template>
            <template v-slot:[`item.adjuster`]="{ item }">{{ item.adjusterName }}</template>
            <template v-slot:[`item.claimStatus`]="{ item }">
              <v-chip small :color="statusColor(item.claimStatus)" dark>{{ item.claimStatus }}</v-chip>
            </template>
          </v-data-table>

          <div v-if="searchPerformed && !searchResults.length && !searching" class="mt-3 text-center caption grey--text">
            No claims found matching your search.
          </div>
        </v-card>
      </div>

      <v-skeleton-loader v-if="loadingForm" type="card, table-heading, list-item-two-line" />

      <!-- ===== CLAIM FORM ===== -->
      <div v-if="claimRows.length" id="claim-form" ref="claimForm" class="claim-form">
        <!-- ===== INITIAL CLAIMS TABLE ===== -->
        <table class="claim-table">
          <thead>
            <tr><td class="t-hdr" colspan="19">ASSOCIATED ADJUSTERS SDN BHD</td></tr>
            <tr class="spacer"><td colspan="19"></td></tr>
            <tr>
              <td class="t-label" colspan="2">NAME :</td>
              <td class="t-val" colspan="4">{{ adjusterName }}</td>
              <td class="t-label" colspan="3">MONTHLY REIMBURSEMENT CLAIMS</td>
              <td class="t-label" colspan="2"></td>
              <td class="t-label">MONTH / YEAR :</td>
              <td class="t-val" colspan="3">{{ reportMonth }}</td>
            </tr>
            <tr class="spacer"><td colspan="18"></td></tr>
            <tr class="t-head">
              <th rowspan="2" width="30">NO.</th>
              <th colspan="2" width="90">REFERENCE</th>
              <th rowspan="2" width="65">VEHICLE NO.</th>
              <th rowspan="2" width="60">INSURER</th>
              <th rowspan="2" width="55">INVOICE NO.</th>
              <th rowspan="2" width="60">INVOICE AMOUNT</th>
              <th rowspan="2" width="55">ADJUSTING FEES</th>
              <th rowspan="2" width="55">PLACE OF LOSS</th>
              <th rowspan="2" width="65">TRAVELLING / MILEAGE</th>
              <th rowspan="2" width="40">ADD.</th>
              <th rowspan="2" width="50">SD &amp; STAMPS</th>
              <th rowspan="2" width="55">MEDICAL REPORT</th>
              <th rowspan="2" width="50">POLICE DOC.</th>
              <th rowspan="2" width="40">JPJ DOC.</th>
              <th rowspan="2" width="40">MISC</th>
              <th rowspan="2" width="40">TYPING</th>
              <th rowspan="2" width="55">TOTAL</th>
            </tr>
            <tr class="t-head">
              <th>TYPE</th>
              <th>FILE NO.</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in claimRows" :key="i" class="t-row">
              <td class="tc">{{ i + 1 }}</td>
              <td class="tc">{{ row.deptCode || '' }}</td>
              <td class="tc">{{ row.caseId || '' }}</td>
              <td class="tc">{{ row.vehicleNo }}</td>
              <td>{{ row.insurer }}</td>
              <td class="tr" style="min-width:65px">{{ row.invNo || '' }}</td>
              <td class="tr">{{ row.invAmount ? fmt(row.invAmount) : '' }}</td>
              <td class="tr">{{ row.adjFees ? fmt(row.adjFees) : '' }}</td>
              <td>{{ row.placeOfLoss }}</td>
              <td class="tr">{{ row.travMileage ? fmt(row.travMileage) : '0.00' }}</td>
              <td class="tr">{{ row.additional ? fmt(row.additional) : '0.00' }}</td>
              <td class="tr">{{ row.sdAndStamps ? fmt(row.sdAndStamps) : '0.00' }}</td>
              <td class="tr">{{ row.medicalReport ? fmt(row.medicalReport) : '0.00' }}</td>
              <td class="tr">{{ row.policeDoc ? fmt(row.policeDoc) : '0.00' }}</td>
              <td class="tr">{{ row.jpjDoc ? fmt(row.jpjDoc) : '0.00' }}</td>
              <td class="tr">{{ row.misc ? fmt(row.misc) : '0.00' }}</td>
              <td class="tr">{{ row.typing ? fmt(row.typing) : '0.00' }}</td>
              <td class="tr b">MYR {{ rowTotal(row) }}</td>
            </tr>
            <tr v-for="n in Math.max(0, 3 - claimRows.length)" :key="'e'+n" class="t-row">
              <td class="tc">{{ claimRows.length + n }}</td>
              <td colspan="2"></td>
              <td colspan="14"></td>
              <td class="tr">0.00</td>
            </tr>
          </tbody>
        </table>

        <!-- ===== FOLLOW UP / SHARED FILES ===== -->
        <table class="claim-table">
          <thead>
            <tr><th class="t-sub-hdr" colspan="18" style="text-align:left;padding-left:8px">FOLLOW UP / SHARED FILES</th></tr>
            <tr class="t-head">
              <th rowspan="2" width="30">NO.</th>
              <th colspan="2" width="90">REFERENCE</th>
              <th rowspan="2" width="65">VEHICLE NO.</th>
              <th rowspan="2" width="60">INSURER</th>
              <th rowspan="2" width="55">INVOICE NO.</th>
              <th rowspan="2" width="60">INVOICE AMOUNT</th>
              <th rowspan="2" width="55">ADJUSTING FEES</th>
              <th rowspan="2" width="55">PLACE OF LOSS</th>
              <th rowspan="2" width="65">TRAVELLING / MILEAGE</th>
              <th rowspan="2" width="40">ADD.</th>
              <th rowspan="2" width="50">SD &amp; STAMPS</th>
              <th rowspan="2" width="55">MEDICAL REPORT</th>
              <th rowspan="2" width="50">POLICE DOC.</th>
              <th rowspan="2" width="40">JPJ DOC.</th>
              <th rowspan="2" width="40">MISC</th>
              <th rowspan="2" width="40">TYPING</th>
              <th rowspan="2" width="55">TOTAL</th>
            </tr>
            <tr class="t-head">
              <th>TYPE</th>
              <th>FILE NO.</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(fu, i) in followUpRows" :key="'fu'+i" class="t-row">
              <td class="tc">{{ i + 1 }}</td>
              <td class="tc"></td>
              <td class="tc"></td>
              <td class="tc"></td>
              <td></td>
              <td></td>
              <td class="tr">{{ fu.invAmount ? fmt(fu.invAmount) : '' }}</td>
              <td class="tr">{{ fu.adjFees ? fmt(fu.adjFees) : '' }}</td>
              <td></td>
              <td class="tr">{{ fu.travMileage ? fmt(fu.travMileage) : '0.00' }}</td>
              <td class="tr">{{ fu.additional ? fmt(fu.additional) : '0.00' }}</td>
              <td class="tr">{{ fu.sdAndStamps ? fmt(fu.sdAndStamps) : '0.00' }}</td>
              <td class="tr">{{ fu.medicalReport ? fmt(fu.medicalReport) : '0.00' }}</td>
              <td class="tr">{{ fu.policeDoc ? fmt(fu.policeDoc) : '0.00' }}</td>
              <td class="tr">{{ fu.jpjDoc ? fmt(fu.jpjDoc) : '0.00' }}</td>
              <td class="tr">{{ fu.misc ? fmt(fu.misc) : '0.00' }}</td>
              <td class="tr">{{ fu.typing ? fmt(fu.typing) : '0.00' }}</td>
              <td class="tr b">MYR {{ followUpTotal(fu) }}</td>
            </tr>
            <!-- GRAND TOTAL row -->
            <tr class="t-row" style="background:#FCE4D6;font-weight:bold">
              <td class="tc" colspan="9">GRAND TOTAL</td>
              <td class="tr">{{ fmt(grandTotal.travMileage) }}</td>
              <td class="tr">{{ fmt(grandTotal.additional) }}</td>
              <td class="tr">{{ fmt(grandTotal.sdAndStamps) }}</td>
              <td class="tr">{{ fmt(grandTotal.medicalReport) }}</td>
              <td class="tr">{{ fmt(grandTotal.policeDoc) }}</td>
              <td class="tr">{{ fmt(grandTotal.jpjDoc) }}</td>
              <td class="tr">{{ fmt(grandTotal.misc) }}</td>
              <td class="tr">{{ fmt(grandTotal.typing) }}</td>
              <td class="tr b">MYR {{ fmt(grandTotal.total) }}</td>
            </tr>
          </tbody>
        </table>

        <!-- ===== BOTTOM SECTION: Signatures (left 30%) + Extra Claims (right 70%) ===== -->
        <div class="bottom-flex">
          <!-- Left: Signatory grid (30%) -->
          <div class="bottom-left">
            <table class="sign-table">
              <tr class="row-header"><td class="t-label">CLAIMANT</td><td class="t-label">CHECKED &amp; APPROVED</td></tr>
              <tr class="row-sign"><td class="sign-space"></td><td class="sign-space"></td></tr>
              <tr class="row-footer"><td class="t-label">ADJUSTER</td><td class="t-label">MANAGER</td></tr>
            </table>
          </div>
          <!-- Right: Extra claim details (70%) -->
          <div class="bottom-right">
            <table class="extra-table">
              <tr><td></td><td class="t-label">SUB - TOTAL</td><td class="tr b amt-cell">{{ fmt(subTotal) }}</td></tr>
              <tr><td>ACCOMODATION</td><td style="white-space:nowrap">@ RM <select v-model="accomRate" class="c-input"><option :value="80">80</option><option :value="100">100</option></select> X <input v-model.number="accomDays" type="number" class="c-input" style="width:30px" /> (DAYS)</td><td class="tr amt-cell">{{ fmt(accomTotal) }}</td></tr>
              <tr><td></td><td>HANDPHONE BILL</td><td class="tr amt-cell"><input v-model.number="handphoneBill" type="number" step="0.01" class="c-input tr" /></td></tr>
              <tr><td></td><td>MONTHLY PARKING FEES</td><td class="tr amt-cell"><input v-model.number="parkingFees" type="number" step="0.01" class="c-input tr" /></td></tr>
              <tr v-for="(spec, i) in specifyRows" :key="'sp'+i"><td>PLEASE SPECIFY :</td><td><select v-model="spec.label" class="c-input" style="width:130px;border-bottom:1px dotted #aaa"><option value="">— Select —</option><option>ACCOMODATION</option><option>CAR RENTAL</option><option>CLEANER SERVICES</option><option>COURIER SERVICE</option><option>FLIGHT TICKET</option><option>INTERNET BILL</option><option>MARKETING EXPENSES</option><option>MONTHLY PARKING FEES</option><option>STATIONERIES</option><option>TOLL</option><option>UP KEEP COMPUTER</option><option>UP KEEP OFFICE</option></select></td><td class="tr amt-cell"><input v-model.number="spec.amount" type="number" step="0.01" class="c-input tr" style="width:60px" /></td></tr>
              <tr><td></td><td>( LESS ) TRAVELLING ADVANCE</td><td class="tr amt-cell"><input v-model.number="travellingAdvance" type="number" step="0.01" class="c-input tr" /></td></tr>
              <tr><td></td><td class="t-label">AMOUNT PAYABLE</td><td class="tr b amt-cell">{{ fmt(amountPayable) }}</td></tr>
            </table>
          </div>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import ClaimsV3DataService from "@/services/ClaimsV3DataService";
import moment from "moment";

export default {
  name: "ClaimsReport",
      data() {
        return {
          loading: true,
          loadingForm: false,
          claimRows: [],
          adjusterName: "",
          reportMonth: moment().format("MMM YYYY"),
          reportDate: moment().format("YYYY-MM"),
          monthMenu: false,
          accomRate: 80,
          accomDays: 0,
          handphoneBill: 0,
          parkingFees: 0,
          followUpRows: Array.from({ length: 3 }, () => ({
            invAmount: 0, adjFees: 0, travMileage: 0, additional: 0,
            sdAndStamps: 0, medicalReport: 0, policeDoc: 0, jpjDoc: 0, misc: 0, typing: 0,
          })),
          specifyRows: [
            { label: "", amount: 0 },
            { label: "", amount: 0 },
            { label: "", amount: 0 },
            { label: "", amount: 0 },
          ],
          travellingAdvance: 0,
          // Search / selector (cart-like)
          searchQuery: "",
          searchAdjuster: "",
          searchStatus: null,
          searching: false,
          searchPerformed: false,
          searchResults: [],
          accumulatedIds: [],
        };
  },
      computed: {
        subTotal() {
          return this.claimRows.reduce((s, r) => s + this.rowTotalNum(r), 0);
        },
    accomTotal() {
      return (this.accomRate || 0) * (this.accomDays || 0);
    },
    specifyTotal() {
      return this.specifyRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    },
        amountPayable() {
          return Math.max(0,
            this.subTotal +
            this.accomTotal +
            (parseFloat(this.handphoneBill) || 0) +
            (parseFloat(this.parkingFees) || 0) +
            this.specifyTotal -
            (parseFloat(this.travellingAdvance) || 0)
          );
        },
            searchHeaders() {
              return [
                { text: "", value: "selected", sortable: false, width: "40" },
                { text: "AASB Reference", value: "aasbRef" },
                { text: "Vehicle No", value: "vehicleNo" },
                { text: "Adjuster", value: "adjusterName" },
                { text: "Status", value: "claimStatus" },
                { text: "Submitted", value: "createdAt" },
              ];
        },
        statusOptions() {
          return [
            { text: "All", value: null },
            { text: "Submitted", value: "SUBMITTED" },
            { text: "Manager Review", value: "MANAGER_REVIEW" },
            { text: "Manager Approved", value: "MANAGER_APPROVED" },
            { text: "Payroll Approved", value: "PAYROLL_APPROVED" },
            { text: "Queried", value: "QUERIED" },
            { text: "Rejected", value: "REJECTED" },
            { text: "Paid", value: "PAID" },
          ];
        },
        grandTotal() {
          const sums = { travMileage: 0, additional: 0, sdAndStamps: 0, medicalReport: 0, policeDoc: 0, jpjDoc: 0, misc: 0, typing: 0, total: 0 };
          this.claimRows.forEach((r) => {
            sums.travMileage += parseFloat(r.travMileage) || 0;
            sums.additional += parseFloat(r.additional) || 0;
            sums.sdAndStamps += parseFloat(r.sdAndStamps) || 0;
            sums.medicalReport += parseFloat(r.medicalReport) || 0;
            sums.policeDoc += parseFloat(r.policeDoc) || 0;
            sums.jpjDoc += parseFloat(r.jpjDoc) || 0;
            sums.misc += parseFloat(r.misc) || 0;
            sums.typing += parseFloat(r.typing) || 0;
            sums.total += this.rowTotalNum(r);
          });
          return sums;
        },
      },
      watch: {
    reportDate(d) {
      this.reportMonth = moment(d).format("MMM YYYY");
    },
  },
  methods: {
    findRef(id, type) {
      if (!id) return null;
      const store = this.$store.state.referenceData;
      const lists = { dept: store.departments, sub: store.subRefTypes, branch: store.branches };
      return (lists[type] || []).find((x) => Number(x.id) === Number(id));
    },
    findIns(id) {
      if (!id) return "";
      const ins = (this.$store.state.referenceData.insurers || []).find((x) => Number(x.id) === Number(id));
      return ins?.name || "";
    },
    rowTotal(r) {
      return this.fmt(this.rowTotalNum(r));
    },
    rowTotalNum(r) {
      return (parseFloat(r.travMileage) || 0) + (parseFloat(r.additional) || 0) +
        (parseFloat(r.sdAndStamps) || 0) + (parseFloat(r.medicalReport) || 0) +
        (parseFloat(r.policeDoc) || 0) + (parseFloat(r.jpjDoc) || 0) +
        (parseFloat(r.misc) || 0) + (parseFloat(r.typing) || 0);
    },
    followUpTotal(fu) {
      return this.fmt(
        (parseFloat(fu.travMileage) || 0) + (parseFloat(fu.additional) || 0) +
        (parseFloat(fu.sdAndStamps) || 0) + (parseFloat(fu.medicalReport) || 0) +
        (parseFloat(fu.policeDoc) || 0) + (parseFloat(fu.jpjDoc) || 0) +
        (parseFloat(fu.misc) || 0) + (parseFloat(fu.typing) || 0) +
        (parseFloat(fu.invAmount) || 0) + (parseFloat(fu.adjFees) || 0)
      );
    },
    recalc() { this.$forceUpdate(); },
    fmt(v) { return parseFloat(v || 0).toFixed(2); },
    async printPDF() {
      const element = document.getElementById("claim-form");
      if (!element) return;
      try {
        const html2pdfModule = await import("html2pdf.js");
        const html2pdf = html2pdfModule.default || html2pdfModule;
        const opt = {
          margin: [8, 8, 8, 8],
          filename: `Claims_Report_${this.reportMonth.replace(/ /g, "_")}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, logging: false },
          jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
          pagebreak: { mode: "avoid-all" },
        };
        html2pdf().set(opt).from(element).save();
      } catch (e) {
        console.error("html2pdf failed:", e);
      }
    },
    async exportXLSX() {
      try {
        const payload = {
          rows: this.claimRows.map((r) => ({
            ...r, rowTotal: this.rowTotalNum(r),
          })),
          adjusterName: this.adjusterName,
          reportMonth: this.reportMonth,
          subTotal: this.subTotal,
          accomRate: this.accomRate,
          accomDays: this.accomDays,
          accomTotal: this.accomTotal,
          handphoneBill: this.handphoneBill,
          parkingFees: this.parkingFees,
          specifyRows: this.specifyRows.filter((s) => s.label),
          travellingAdvance: this.travellingAdvance,
          amountPayable: this.amountPayable,
        };
        const res = await ClaimsV3DataService.exportXlsx(payload);
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = `Claims_Report_${this.reportMonth.replace(/ /g, "_")}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        console.error("XLSX export failed, using fallback", e);
        // Fallback: client-side CSV
        const data = [];
        data.push(["ASSOCIATED ADJUSTERS SDN BHD"]);
        data.push(["NAME:", this.adjusterName, "", "", "", "MONTH/YEAR:", this.reportMonth]);
        data.push([]);
        data.push(["NO.", "TYPE", "FILE NO.", "VEHICLE NO.", "INSURER", "INVOICE NO.",
          "INVOICE AMOUNT", "ADJ. FEES", "PLACE OF LOSS", "TRAVELLING", "ADD.",
          "SD & STAMPS", "MEDICAL", "POLICE", "JPJ", "MISC", "TYPING", "TOTAL"]);
        this.claimRows.forEach((r, i) => {
          data.push([i + 1, r.deptCode, r.caseId, r.vehicleNo, r.insurer, r.invNo,
            r.invAmount, r.adjFees, r.placeOfLoss, r.travMileage, r.additional,
            r.sdAndStamps, r.medicalReport, r.policeDoc, r.jpjDoc, r.misc, r.typing, this.rowTotalNum(r)]);
        });
        data.push([]);
        data.push(["SUB-TOTAL", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", this.subTotal]);
        data.push(["ACCOMODATION", "", "", "", "", "", "", `@ RM${this.accomRate} x ${this.accomDays} days`, "", "", "", "", "", "", "", "", "", this.accomTotal]);
        data.push(["HANDPHONE BILL", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", this.handphoneBill]);
        data.push(["PARKING FEES", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", this.parkingFees]);
        this.specifyRows.forEach((s) => { if (s.label) data.push(["SPECIFY: " + s.label, "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", s.amount]); });
        data.push(["(LESS) ADVANCE", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", this.travellingAdvance]);
        data.push(["AMOUNT PAYABLE", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", "", this.amountPayable]);
        import("xlsx").then((XLSX) => {
          const wb = XLSX.utils.book_new();
          const ws = XLSX.utils.aoa_to_sheet(data);
          ws['!cols'] = [
            { wch: 5 }, { wch: 8 }, { wch: 8 }, { wch: 12 }, { wch: 10 },
            { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 },
            { wch: 8 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 8 },
            { wch: 8 }, { wch: 8 }, { wch: 12 },
          ];
          ws['!pageSetup'] = { orientation: 'landscape', paperSize: 9, fitToPage: true, fitToWidth: 1 };
          XLSX.utils.book_append_sheet(wb, ws, "Claims Report");
          XLSX.writeFile(wb, `Claims_Report_${this.reportMonth.replace(/ /g, "_")}.xlsx`);
        }).catch(() => {
          const csv = data.map(r => r.join(",")).join("\n");
          const blob = new Blob([csv], { type: "text/csv" });
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `Claims_Report_${this.reportMonth.replace(/ /g, "_")}.csv`;
          a.click();
        });
      }
    },
    async exportDOCX() {
      try {
        const payload = {
          rows: this.claimRows.map((r) => ({
            ...r, rowTotal: this.rowTotalNum(r),
          })),
          adjusterName: this.adjusterName,
          reportMonth: this.reportMonth,
          subTotal: this.subTotal,
          accomRate: this.accomRate,
          accomDays: this.accomDays,
          accomTotal: this.accomTotal,
          handphoneBill: this.handphoneBill,
          parkingFees: this.parkingFees,
          specifyRows: this.specifyRows.filter((s) => s.label),
          travellingAdvance: this.travellingAdvance,
          amountPayable: this.amountPayable,
        };
        const res = await ClaimsV3DataService.exportDocx(payload);
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = `Claims_Report_${this.reportMonth.replace(/ /g, "_")}.docx`;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        console.error("DOCX export failed", e);
        // Fallback: client-side HTML doc (original approach)
        const formEl = document.getElementById("claim-form");
        if (!formEl) return;
        const clone = formEl.cloneNode(true);
        clone.querySelectorAll("input").forEach((el) => {
          const val = el.value || "0";
          const text = document.createTextNode(val);
          el.parentNode.replaceChild(text, el);
        });
        clone.querySelectorAll("select").forEach((el) => {
          const text = document.createTextNode(el.options[el.selectedIndex]?.text || "");
          el.parentNode.replaceChild(text, el);
        });
        const html = clone.innerHTML;
        const blob = new Blob([
          `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
          <head><meta charset="utf-8">
          <!--[if gte mso 9]>
          <xml>
            <w:WordDocument>
              <w:View>Print</w:View>
              <w:Zoom>100</w:Zoom>
              <w:DoNotOptimizeForBrowser/>
            </w:WordDocument>
          </xml>
          <![endif]-->
          <style>
          @page Section1 { size: 11in 8.5in; mso-page-orientation: landscape; margin: 0.3in 0.5in; }
          div.Section1 { page: Section1; }
          body { font-family: 'Arial Narrow', Arial, sans-serif; font-size: 10pt; }
          table { border-collapse: collapse; width: 100%; }
          td, th { border: 1px solid #999; padding: 3px; font-size: 9pt; }
          .t-hdr { font-weight: bold; font-size: 13pt; text-align: center; background: #f2f2f2; }
          .t-head th { background: #f2f2f2; font-weight: bold; font-size: 8pt; text-align: center; }
          .t-row td { border: 1px dotted #aaa; font-size: 9pt; }
          .tc { text-align: center; } .tr { text-align: right; } .b { font-weight: bold; }
          .t-label { font-weight: bold; font-size: 9pt; border: none !important; }
          .t-sign { font-size: 10pt; border: none !important; }
          .t-sub-hdr { font-weight: bold; font-size: 9pt; background: #f2f2f2; }
          .no-print { display: none; }
          .amt-cell { background: #FCE4D6; }
          </style></head><body><div class="Section1">${html}</div></body></html>`
        ], { type: "application/msword" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `Claims_Report_${this.reportMonth.replace(/ /g, "_")}.doc`;
        a.click();
      }
    },
    async searchClaims() {
      this.searching = true;
      this.searchPerformed = true;
      try {
        const params = { size: 50, search: this.searchQuery || undefined };
        if (this.searchStatus) params.claimStatus = this.searchStatus;
        if (this.searchAdjuster) {
          // Find user by name search — for now pass as adjId if numeric, else fallback
          const members = this.$store.state.referenceData.members || [];
          const matched = members.find((m) =>
            (m.fullname || m.username || "").toLowerCase().includes(this.searchAdjuster.toLowerCase())
          );
          if (matched) params.adjId = matched.id;
        }
        const res = await ClaimsV3DataService.getAll(params);
        const rows = res.data?.rows || [];
        // Also fetch adjuster names for display
        const members = this.$store.state.referenceData.members || [];
        this.searchResults = rows.map((r) => {
          const adj = members.find((m) => m.id === r.adjId);
          return {
            id: r.id,
            aasbRef: this.formatAasbRef(r),
            vehicleNo: (r.casefile?.vehicleNo || "").toUpperCase(),
            adjusterName: adj?.fullname || adj?.username || `User #${r.adjId}`,
            claimStatus: r.claimStatus,
            createdAt: r.createdAt ? moment(r.createdAt).format("DD/MM/YYYY") : "",
            _raw: r,
          };
        });
      } catch (e) {
        console.error("Search failed", e);
      } finally {
        this.searching = false;
      }
    },
    isSelected(id) {
      return this.accumulatedIds.includes(id);
    },
    toggleClaim(item) {
      const id = item.id;
      if (this.accumulatedIds.includes(id)) {
        this.accumulatedIds = this.accumulatedIds.filter((i) => i !== id);
      } else {
        this.accumulatedIds = [...this.accumulatedIds, id];
      }
    },
    async generateReport() {
      if (!this.accumulatedIds.length) return;
      this.loadingForm = true;
      this.claimRows = [];
      this.adjusterName = "";
      try {
        const rows = [];
        for (const id of this.accumulatedIds) {
          const res = await ClaimsV3DataService.get(id);
          const c = res.data;
          const cf = c.casefile || {};
          const dept = this.findRef(cf.refType, "dept");
          rows.push({
            id: c.id,
            caseId: cf.id || c.caseId,
            deptCode: dept?.name || "",
            vehicleNo: (cf.vehicleNo || "").toUpperCase(),
            insurer: this.findIns(cf.insurer),
            placeOfLoss: cf.placeOfLoss || "",
            invNo: cf.invNo || "",
            invAmount: parseFloat(cf.invTotal) || 0,
            adjFees: parseFloat(cf.invFeeNote) || 0,
            travMileage: parseFloat(c.travMileage) || 0,
            additional: parseFloat(c.additional) || 0,
            sdAndStamps: parseFloat(c.sdAndStamps) || 0,
            medicalReport: parseFloat(c.medicalReport) || 0,
            policeDoc: parseFloat(c.policeDoc) || 0,
            jpjDoc: parseFloat(c.jpjDoc) || 0,
            misc: parseFloat(c.misc) || 0,
            typing: parseFloat(c.typing) || 0,
          });
          if (!this.adjusterName) {
            const user = this.$store.state.referenceData.members?.find((m) => m.id === c.adjId);
            this.adjusterName = user?.fullname || user?.username || `User #${c.adjId}`;
          }
        }
        this.claimRows = rows;
        // Scroll to form
        this.$nextTick(() => {
          const el = document.getElementById("claim-form");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      } catch (e) {
        console.error("Failed to load claims", e);
      } finally {
        this.loadingForm = false;
      }
    },
    clearSelection() {
      this.accumulatedIds = [];
      this.claimRows = [];
      this.searchResults = [];
      this.searchPerformed = false;
    },
    formatAasbRef(item) {
      const cf = item.casefile || item._raw?.casefile || {};
      const dept = this.findRef(cf.refType, "dept");
      const sub = this.findRef(cf.subRefType, "sub");
      const br = this.findRef(cf.branch, "branch");
      const yr = cf.dateOfAssign ? moment(cf.dateOfAssign).format("YY") : "";
      return `AA/${dept?.name || ""}${sub ? "/" + sub.subCode : ""}/${cf.id || cf.caseId || ""}/${yr}${br ? "/" + br.brCode : ""}`;
    },
    statusColor(s) {
      const colors = { SUBMITTED: "grey", MANAGER_REVIEW: "orange", QUERIED: "warning", MANAGER_APPROVED: "blue", PAYROLL_REVIEW: "purple", PAYROLL_APPROVED: "teal", REJECTED: "error", PAID: "green" };
      return colors[s] || "grey";
    },
  },
  async mounted() {
    this.loading = false;
    await this.$store.dispatch("referenceData/ensureRefs");
    // If navigated from dashboard with selected IDs, load them automatically
    if (this.$route.query.ids) {
      this.accumulatedIds = this.$route.query.ids.split(",").map(Number);
      await this.generateReport();
    }
  },
};
</script>

<style scoped>
@media print {
  @page { size: landscape A4; margin: 0.3in; }
  .no-print { display: none !important; }
  #report-container { box-shadow: none !important; padding: 0 !important; }
  .claim-form { max-width: 100%; }
}
.claim-form { font-family: 'Arial Narrow', Arial, sans-serif; font-size: 10pt; max-width: 1152px; margin: 0 auto; }
.claim-table { border-collapse: collapse; width: 100%; margin-bottom: 2px; }
.t-hdr { font-weight: bold; font-size: 13pt; text-align: center; padding: 5px; border: 1px solid #000; background: #f2f2f2; }
.t-head th { background: #f2f2f2; font-weight: bold; font-size: 9pt; text-align: center; border: 1px solid #000; padding: 4px; vertical-align: middle; }
.t-row td { border: 0.5pt dotted #A6A6A6; font-size: 10pt; padding: 3px 5px; vertical-align: middle; }
.tc { text-align: center; }
.tr { text-align: right; }
.b { font-weight: bold; }
.t-label { font-weight: bold; font-size: 10pt; border: none !important; padding: 2px; }
.t-sign { font-size: 10pt; border: none !important; padding: 2px; }
.t-val { font-weight: bold; font-size: 10pt; border: none !important; padding: 2px; }
.t-sub-hdr { font-weight: bold; font-size: 10pt; border: 0.5pt dotted #A6A6A6; background: #f2f2f2; padding: 4px; }
.bottom-table td { border: 0.5pt dotted #A6A6A6; font-size: 10pt; padding: 3px 5px; }
.spacer td { height: 6px; border: none !important; }
.c-input { border: none; border-bottom: 1px dotted #ccc; width: 100%; font-size: 10pt; background: transparent; text-align: inherit; }
.c-input:focus { outline: none; border-bottom: 1px solid #1976d2; background: #e3f2fd; }
/* Bottom flex: 30% + 70% */
.bottom-flex { display: flex; width: 100%; margin-top: 6px; }
.bottom-left { width: 30%; padding-right: 8px; display: flex; }
.bottom-right { width: 70%; }
.sign-table { width: 100%; height: 100%; border-collapse: collapse; border: 1px solid #000; table-layout: fixed; }
.sign-table td { border: 1px solid #000; padding: 8px; text-align: center; vertical-align: middle; }
.sign-table td.t-label { border: 1px solid #000 !important; font-size: 10pt; background: #f2f2f2; }
.sign-table tr.row-header { height: 20%; }
.sign-table tr.row-sign { height: 60%; }
.sign-table tr.row-footer { height: 20%; }
.sign-table .sign-space { height: 100%; }
.extra-table { width: 100%; border-collapse: collapse; border: 1px solid #000; }
.extra-table td { border-bottom: 0.5pt dotted #A6A6A6 !important; font-size: 10pt; padding: 3px 6px; }
.extra-table td select,
.extra-table td input { display: inline-block; width: auto; }
.extra-table .amt-cell { border: 0.5pt dotted #A6A6A6 !important; background: #FCE4D6; font-weight: bold; text-align: right; }
.extra-table .amt-cell input { background: #FCE4D6; font-weight: bold; text-align: right; }
input[type=number] { -moz-appearance: textfield; }
input::-webkit-outer-spin-button, input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.amt-cell { background: #FCE4D6; }
.amt-total { background: #FCE4D6; font-weight: bold; }
</style>
